import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AppleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { WizardService } from "../../../services/WizardService";
import { SpmInfo } from "../../../types/spmInfo";

const { Text } = Typography;

export interface SpmSourceCardProps {
  mappingId: number;
  engine: string;
  /**
   * Read-only: the CocoaPods pod this SPM source replaces at render time. Derived from the
   * Mapping (`<engine>_dependency_ios`, or `own_dependency_ios` for the core row) — NOT stored on
   * SpmInfo. Shown for clarity; the crawler resolves the same value from the Mapping.
   */
  replacesPod?: string;
}

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong";

/**
 * Inline editor for the (at most one) `SpmInfo` source row scoped to a `(mappingId, engine)`
 * pair — the backend enforces a unique index on that pair (`uq_spm_info_v1_mapping_engine`),
 * so this is a single optional `{ url, product }` record per mediation card, not a list.
 *
 * All actions (add/edit/delete) call `v4/spm` immediately — independent of the parent Mapping
 * form's Save button.
 */
export const SpmSourceCard = (props: SpmSourceCardProps) => {
  const { mappingId, engine, replacesPod } = props;
  const api = WizardService.SpmInfo;

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [row, setRow] = useState<SpmInfo | undefined>(undefined);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [form] = Form.useForm();

  const fetchRow = useCallback(() => {
    setLoading(true);
    api
      .all(
        new URLSearchParams({ mapping_id: String(mappingId), engine, per: "1" })
      )
      .then((response) => setRow((response.items || [])[0]))
      .catch((error) => message.error(extractErrorMessage(error)))
      .finally(() => setLoading(false));
  }, [api, mappingId, engine]);

  useEffect(() => {
    fetchRow();
  }, [fetchRow]);

  const startEdit = () => {
    form.setFieldsValue({ url: row?.url || "", product: row?.product || "" });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    form.resetFields();
  };

  const submit = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      const payload: SpmInfo = {
        id: row?.id,
        mappingId,
        engine,
        url: values.url,
        product: values.product,
      };
      const promise = row?.id
        ? api.update(row.id, payload)
        : api.create(payload);

      promise
        .then((saved) => {
          setRow(saved);
          setEditing(false);
          message.success("SPM source saved");
        })
        .catch((error) => {
          message.error(extractErrorMessage(error));
          // Refresh in case a concurrent edit changed server state (e.g. duplicate conflict).
          fetchRow();
        })
        .finally(() => setSaving(false));
    });
  };

  const remove = () => {
    if (!row?.id) return;
    setSaving(true);
    api
      .delete(row.id)
      .then(() => {
        setRow(undefined);
        message.success("SPM source deleted");
      })
      .catch((error) => message.error(extractErrorMessage(error)))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Divider style={{ margin: "0.75rem 0" }} />
      <Space
        style={{ marginBottom: "0.5rem", width: "100%" }}
        align="center"
      >
        <AppleOutlined />
        <Text strong>SPM Source</Text>
        {!isLoading && !isEditing && (
          <Tag color={row ? "green" : "default"} style={{ marginLeft: 4 }}>
            {row ? "Configured" : "Not configured"}
          </Tag>
        )}
      </Space>
      <Text
        type="secondary"
        style={{ display: "block", fontSize: "0.85em", marginBottom: "0.5rem" }}
      >
        Replaces pod: {replacesPod?.trim() ? <Text code>{replacesPod}</Text> : "—"}{" "}
        <Text type="secondary" style={{ fontSize: "0.9em" }}>
          (auto from Mapping)
        </Text>
      </Text>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : isEditing ? (
        // component={false}: avoid nesting a native <form> inside the outer Mapping <Form>.
        <Form form={form} layout="vertical" component={false}>
          <Form.Item
            name="url"
            label="Git URL"
            rules={[
              { required: true, message: "Please input the repository URL" },
              { pattern: /\.git$/, message: "URL must end with .git" },
            ]}
          >
            <Input placeholder="https://github.com/appodeal/Appodeal-Swift-Package.git" />
          </Form.Item>
          <Form.Item
            name="product"
            label="Product"
            rules={[
              { required: true, message: "Please input the SPM product name" },
            ]}
          >
            <Input placeholder="AppLovinMediationGoogleAdapter" />
          </Form.Item>
          <Space>
            <Button type="primary" loading={isSaving} onClick={submit}>
              Save
            </Button>
            <Button htmlType="button" onClick={cancelEdit}>
              Cancel
            </Button>
          </Space>
          <Text
            type="secondary"
            style={{ display: "block", fontSize: "0.85em", marginTop: 8 }}
          >
            Saves immediately, independent of the Save button above.
          </Text>
        </Form>
      ) : row ? (
        <Space
          direction="vertical"
          size={6}
          style={{
            width: "100%",
            padding: "0.5rem 0.75rem",
            borderRadius: 6,
            border: "1px solid rgba(128, 128, 128, 0.25)",
          }}
        >
          <Text
            code
            copyable={{ text: row.url }}
            style={{ wordBreak: "break-all" }}
          >
            {row.url}
          </Text>
          <Tag color="cyan" style={{ width: "fit-content" }}>
            {row.product}
          </Tag>
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={startEdit}>
              Edit
            </Button>
            <Popconfirm
              title="Delete SPM source"
              description="Are you sure you want to delete this SPM source?"
              onConfirm={remove}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={isSaving}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      ) : (
        <Button
          type="dashed"
          block
          size="small"
          icon={<PlusOutlined />}
          onClick={startEdit}
        >
          Add SPM source
        </Button>
      )}
    </>
  );
};
